import { AnimatePresence, motion } from "framer-motion";
import { AnimateMotion } from "@/components/AnimateMotion";
import { VisualArea as VisualAreaWrapper } from "@/components/layout/VisualArea";
import { VisualAreaBottom as VisualAreaBottomWrapper } from "@/components/layout/VisualAreaBottom";
import LoadingScreen from "@/components/LoadingScreen";
import { useImproveLoading, useHasImproveInitialized } from "@/store";
import { useMobile } from "@/hooks/useMobile";
import { useCoreApp } from "@/contexts/CoreAppContext";
import { DEFAULT_DURATION } from "@/constants/motion";

function ImproveSidebar({ children }: { children: React.ReactNode }) {
    const { hideSidebar } = useCoreApp();
    const isMobile = useMobile();

    return (
        <div className="relative flex h-full w-[340px] flex-shrink-0 flex-col p-6 pl-0">
            <motion.div
                className="shadow-soft relative flex h-full flex-col rounded-xl border border-gray-300 bg-white"
                initial={false}
                animate={{
                    ...(isMobile && { height: hideSidebar ? 0 : "auto" }),
                    paddingTop: hideSidebar ? 0 : undefined,
                    paddingBottom: hideSidebar ? 0 : undefined
                }}
                transition={{
                    duration: DEFAULT_DURATION.slower,
                    ease: "easeInOut",
                    ...(isMobile && { height: { duration: DEFAULT_DURATION.slower } })
                }}
                style={{ overflow: "hidden" }}
            >
                <AnimatePresence mode="wait">
                    {!hideSidebar && (
                        <motion.div
                            key="sidebar-content"
                            initial={{ opacity: 0, y: -20 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -20 }}
                            transition={{ duration: DEFAULT_DURATION.slower, ease: "easeOut" }}
                            className="sidebarBody hideOnMobile"
                        >
                            {children}
                        </motion.div>
                    )}
                </AnimatePresence>
            </motion.div>
        </div>
    );
}

export function ImproveLayout({
    Sidebar,
    VisualArea,
    BottomContent
}: {
    Sidebar?: React.ReactNode;
    VisualArea: React.ReactNode;
    BottomContent?: React.ReactNode;
}) {
    const loading = useImproveLoading();
    const hasInitialized = useHasImproveInitialized();

    if (loading || !hasInitialized) {
        return <LoadingScreen message="Loading images to review..." />;
    }

    return (
        <AnimateMotion className="h-full w-full bg-white">
            <div className="relative flex h-[calc(100vh-6rem)] flex-col">
                <div className="relative flex h-full w-full min-w-0 flex-col md:flex-row">
                    <div className="relative flex h-full min-w-0 flex-1 flex-col px-3 pb-1 md:p-0">
                        <div className="relative flex h-full min-w-0 flex-1">
                            <VisualAreaWrapper>{VisualArea}</VisualAreaWrapper>
                        </div>
                        {BottomContent && (
                            <div className="relative w-screen md:w-full">
                                <VisualAreaBottomWrapper>{BottomContent}</VisualAreaBottomWrapper>
                            </div>
                        )}
                    </div>
                    <ImproveSidebar>{Sidebar}</ImproveSidebar>
                </div>
            </div>
        </AnimateMotion>
    );
}
